<template>
	<div ref="dropdownWrapper" class="dropdown-wrapper" :class="{'tertiary': variant === 'tertiary'}">
		<!-- Dropdown -->
		<div class="dropdown-content" v-click-outside="clickOutsideHandler">
			<!-- Tertiary Button -->
			<button-tertiary
            v-if="variant === 'tertiary'"
			ref="iconButtonRef"
            withExternalLabel
            :buttonSize="buttonSize || 'small'"
            :buttonStyle="buttonStyle || 'round'"
			:icon="icon || 'more-settings'"
			:label="hideLabelOnButton ? null : label"
            :disabled="disabled"
			@clicked="moreOptionsOpenHandler"/>
			<!-- Default More Options button -->
			<button-quaternary
            v-else
            ref="iconButtonRef"
            :icon="icon || 'more'"
            :disabled="disabled"
            @clicked="moreOptionsOpenHandler"/>
			<!-- *********************************************
			*                  	Web Draw Body                *
			********************************************** -->
			<teleport to='#teleport-dropdown-target' :disabled="!tableWithScrollParent">
                <transition name="opening" @before-enter="beforeEnterHandler">
                    <div
                    v-if="dropdownIsOpen && !getMobileAppState"
                    class="dropdown-animation-wrapper dont-close-side-modals"
                    :class="{'fixed-position': applyPositionFixed}"
                    :style="applyPositionFixedStyles">
                        <div class="dropdown-body">
                            <dropdown-row
                            v-for="row in options"
                            :key="row.id"
                            :option="row"
                            @clicked="[emit('clicked', $event), clickOutsideHandler()]"/>
                        </div>
                    </div>
                </transition>
            </teleport>
			<!-- *********************************************
			*                 	Mobile Draw Body             *
			********************************************** -->
			<teleport to='#more-options-mobile-layer-overlay-teleport'>
				<transition name="opening-drawer">
					<div
					v-if="dropdownIsOpen && getMobileAppState"
					class="dropdown-mobile-drawer-background-layer dont-close-side-modals"
					@click="[emit('showingMoreOptions', false), dropdownIsOpen = false]">
						<div class="dropdown-animation-wrapper-mobile-drawer">
							<div class="drawer-body-title">{{label}}</div>
							<!-- Body -->
							<div class="dropdown-body">
								<dropdown-row
								v-for="row in options"
								:key="row.id"
								:option="row"
								@clicked="[emit('clicked', $event), clickOutsideHandler()]"/>
							</div>
						</div>
					</div>
				</transition>
			</teleport>
		</div>
	</div>
</template>

<script setup>
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import ButtonQuaternary from '@/components/generic/buttons/Quaternary'
import DropdownRow from '@/components/generic/dropdowns/moreOptions/Row'
/* Vue */
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
/* Assets */
import { validateIfParentExists } from '@/assets/scripts/htmlManipulators.js'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
const { updatePreventMobileFeatureBack } = mapActions()
const { getMobileAppState, getFooterState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['showingMoreOptions', 'clicked', 'removeTriggerOpen'])

/** props received
@variant { String } can be ['tertiary', 'quaternary' (default)]
@buttonStyle {String} can be [square (default), round]
@buttonSize {string} can be [small (default), tiny]
@icon { String } icon for button
@label { String } label for button tertiary (if neeeded)
@disabled { Boolean }
@hideLabelOnButton {Boolean} prop to force label to be hid on button and only be showed on more-options title for mobile
@options { Array } each item is an Object
	- id { String, Number }
	- text { String }
	- icon { String }
@triggerOpen { Boolean }
@applyTableTeleport {Boolean} - prop to display dropdown body in a teleport in table
*/
const props = defineProps({
    variant: String,
    buttonStyle: String,
    buttonSize: String,
	icon: String,
	label: String,
    disabled: Boolean,
    hideLabelOnButton: Boolean,
	options: Array,
	triggerOpen: Boolean,
    applyTableTeleport: Boolean
})

const dropdownIsOpen = ref(false)
const dropdownWrapper = ref('')
const iconButtonRef = ref('')

/*************************************************
*                                                *
*                Open/Close Logic                *
*                                                *
*************************************************/
const applyPositionFixed = ref(false)
const applyPositionFixedStyles = ref(null)
const tableWithScrollParent = ref(null)

const validateIfApplyPositionFixed = () => {
	tableWithScrollParent.value = validateIfParentExists(dropdownWrapper.value, 'table-wrapper-for-scroll')

	if (!tableWithScrollParent.value) return
    applyPositionFixed.value = true
}

/**
* Here we check if the distance to the bottom is bigger than the dropdown size
*/
const beforeEnterHandler = (el) => {
	let distanceToBottom
	let distanceToLeft

	const valueToBreathe = 20 // spacing between dropdown and bottom plus dropdown and input
	const maxHeight = 288 // same value we have @css
	const footerHeight = getFooterState.value ? 85 : 0

	if (tableWithScrollParent.value && applyPositionFixed.value) {
        const dropdownWrapperStyles = dropdownWrapper.value.getBoundingClientRect()
        const rowWrapper = validateIfParentExists(dropdownWrapper.value, 'data-table-row')
        const tableWrapper = validateIfParentExists(dropdownWrapper.value, 'wrapping')

        distanceToBottom = window.innerHeight - tableWrapper.getBoundingClientRect().top - rowWrapper.offsetTop - rowWrapper.clientHeight

        applyPositionFixedStyles.value = {
            position: 'fixed',
            right: '50px',
            top: `${dropdownWrapperStyles.top - (distanceToBottom < 80 ? (30 + distanceToBottom) : 50)}px`, // dropdown-cell padding top + space between body and dropdown top
            width: 'fit-content',
            'z-index': 10
        }

		return
	} else {
		/* We are using header top to calc distanceToBottom, because our body starts on top of our header */
		distanceToBottom = window.innerHeight - iconButtonRef.value.$el.getBoundingClientRect().top - iconButtonRef.value.$el.clientHeight - valueToBreathe - footerHeight
		/* Check If inside router-content-wrapper and with menu at right */
		if (validateIfParentExists(iconButtonRef.value, 'router-content-wrapper')) {
			distanceToLeft = iconButtonRef.value.$el.getBoundingClientRect().left
			if (validateIfParentExists(iconButtonRef.value, 'has-side-menu')) distanceToLeft = distanceToLeft - 260 /* Menu width */
		}
	}

	if (distanceToLeft <= 75) {
		el.style.left = 0
		el.style.right = 'unset'
	}

	/* If we have space for our dropdown nothing is need */
	if (distanceToBottom > maxHeight) return

	/** Fliping dropdown up */
	el.style.top = 'unset'
	el.style.bottom = '-20px'
}

const moreOptionsOpenHandler = () => {
	/* This is to force other more-options closing when clicking another more-options, for some reason it doesnt trigger click-outside */
	if (document.getElementsByClassName('features-base-content-wrapper').length) {
		document.getElementsByClassName('features-base-content-wrapper')[0].classList.add('dont-close-side-modals')
		document.getElementsByClassName('features-base-content-wrapper')[0].click()
		document.getElementsByClassName('features-base-content-wrapper')[0].classList.remove('dont-close-side-modals')
	}
	if (document.getElementsByClassName('home-feature-specific-wrapper').length) document.getElementsByClassName('home-feature-specific-wrapper')[0].click()
	/* ***** ******* ******* */
	emit('showingMoreOptions', true)
	dropdownIsOpen.value = true
	/* Preving mobile back to trigger features close */
	updatePreventMobileFeatureBack(true)
}

const clickOutsideHandler = $event => {
	if (!dropdownIsOpen.value) return

	if (props.triggerOpen || ($event && (validateIfParentExists($event?.target, 'dropdown-mobile-drawer-background-layer') ||
		$event?.target.className === 'dropdown-mobile-drawer-background-layer'))) {
		/* if we have triggerOpen a true, we now want to reset it */
		if (props.triggerOpen) emit('removeTriggerOpen')
		return
	}

	emit('showingMoreOptions', false)
	dropdownIsOpen.value = false
}

watch(() => props.triggerOpen, (newVal, oldVal) => {
	if (props.triggerOpen) moreOptionsOpenHandler()
})

onMounted(() => {
	validateIfApplyPositionFixed()
})

/***********************************************************
*                                                          *
*                 MOBILE NATIVE BACK LOGIC                 *
*                                                          *
***********************************************************/
onBeforeUnmount(() => {
	updatePreventMobileFeatureBack(false)
})

const onBackKeyDown = () => {
    clickOutsideHandler()
}

document.addEventListener('backbutton', onBackKeyDown, false)
</script>

<style lang="scss" scoped>
.dropdown-wrapper {
	width: 40px;
	min-height: 40px;

	display: flex;
	flex-direction: column;

	position: relative;

	&.tertiary {
		width: fit-content;
	}

	.dropdown-content {
		width: 100%;

		position: relative;

		.opening-enter-active, .opening-leave-active{
			opacity: 1;
			transform: translateZ(0);
		}

		.opening-enter-from, .opening-leave-to{
			opacity: 0;
			transform: translateZ(-1400px);
		}
	}
}

.dropdown-animation-wrapper {
    @include generic-scrollbar(var(--white), var(--primary));
    width: auto;
    min-width: 156px;
    max-width: 300px;
    max-height: 288px;

    display: flex;
    flex-direction: column;

    overflow: auto;

    opacity: 1;
    transform: translateZ(0);
    transition: all .2s cubic-bezier(0.250, 0.460, 0.450, 0.940);

    position: absolute;
    top: -16px;
    right: 0;
    z-index: 5;

    background-color: var(--secondary-light-4);
    box-shadow: var(--box-shadow-2);
    overflow-x: hidden;
    border-radius: 4px;

    &.fixed-position {
        position: fixed;
    }
}

.dropdown-mobile-drawer-background-layer {
	@include moreOptionsMobilelayerOverlayTeleportStyles;
	flex-direction: column;
	opacity: 1;
	transition: opacity 0.1s linear;

	.dropdown-animation-wrapper-mobile-drawer {
		width: 100%;
		padding: 16px 0;

		display: flex;
		flex-direction: column;

		box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.08);
		// box-shadow: 0px -2px 10px 0px rgba(0, 0, 0, 0.15); dark
		background-color: var(--secondary-light-4);
		border-radius: 16px 16px 0px 0px;
		transition: all 0.3s linear;

		.drawer-body-title {
			width: 100%;
			padding: 0 16px;
			margin-bottom: 12px;

			@include H5-Medium;
			color: var(--secondary-dark-3);
		}
	}

	&.opening-drawer-enter-active,
	&.opening-drawer-leave-active {
		opacity: 1;
		.dropdown-animation-wrapper-mobile-drawer {
			transform: translateY(0);
		}
	}

	&.opening-drawer-enter-from,
	&.opening-drawer-leave-to {
		opacity: 0;
		.dropdown-animation-wrapper-mobile-drawer {
			transform: translateY(110%);
		}
	}
}
</style>
